/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/
import { useEffect } from 'react'

import Card from '@mui/material/Card'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'

import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import { useAuth } from 'context/auth'
import { updateRoutes, useMaterialUIController } from 'context/materialUI'
import BusinessAccount from 'layouts/business-account'
import DashboardLayout from 'modules/LayoutContainers/DashboardLayout'
import LoadingComponent from 'modules/Loading'
import DashboardNavbar from 'modules/Navbars/DashboardNavbar'
import DataTable from 'modules/Tables/DataTable'
import { ROUTE_PATHS, RouteType } from 'routes'
import { TabularData } from 'types/server/tabular'

function BusinessAccountTable(): JSX.Element {
	const [controller, dispatch] = useMaterialUIController()
	const { user } = useAuth()
	const navigate = useNavigate()

	const { routes } = controller

	const fetchData = async () => {
		const response = await axios.get<TabularData>(
			`${process.env.REACT_APP_API_URL}/business-account/table`,
			{
				headers: {
					Authorization: await user.auth.getIdToken(),
				},
			}
		)
		return response.data
	}

	const { data, error, isLoading } = useQuery({
		queryKey: ['business-account-table'],
		queryFn: fetchData,
	})

	useEffect(() => {
		if (!data) return

		const businessAccountRoutes = data.table.rows.map(account => {
			const route: RouteType = {
				type: 'invisible',
				name: `Business Account: ${account.id}`,
				key: `business-account-${account.id}`,
				route: `${ROUTE_PATHS.BUSINESS_ACCOUNTS.BASE}/${account.id}`,
				component: <BusinessAccount />,
				authRequired: true,
			}
			return route
		})

		updateRoutes(dispatch, routes, businessAccountRoutes)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, dispatch])

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<MDBox pt={6} pb={3}>
				<Card>
					<MDBox
						display={'flex'}
						flexDirection={'row'}
						alignItems={'center'}
						justifyContent={'space-between'}
						p={3}
					>
						<MDBox lineHeight={1}>
							<MDTypography variant="h2" fontWeight="medium">
								Business Accounts
							</MDTypography>
							<MDTypography variant="button" color="text">
								View and manage all your business accounts.
							</MDTypography>
						</MDBox>
						{user.data.role !== 'VIEWER' && (
							<MDButton
								variant="outlined"
								color="primary"
								onClick={() => navigate(ROUTE_PATHS.BUSINESS_ACCOUNTS.NEW)}
							>
								+ New Account
							</MDButton>
						)}
					</MDBox>
					{!isLoading && !error ? (
						<DataTable
							table={data.table}
							parentPath={ROUTE_PATHS.BUSINESS_ACCOUNTS.BASE}
							canSearch
							filters={data.filters}
						/>
					) : (
						<LoadingComponent type="box" height={600} containerStyle={{ padding: 50 }} />
					)}
				</Card>
			</MDBox>
		</DashboardLayout>
	)
}

export default BusinessAccountTable
